<template>
  <BaseModule
    v-bind="$props"
    class="services"
    data-module="services"
  >
    <TextAndMedia
      v-bind="$props"
      :text="servicesList"
      :slides="allSlides"
    >
      <template #title>
        <component
          :is="titleTag"
          class="tit tit-g"
        >
          {{ title }}
        </component>
      </template>
    </TextAndMedia>
  </BaseModule>
</template>

<script>
import { mapGetters } from 'vuex';
import base from '@/mixins/base';
import getApiHotels from '@/mixins/getApiHotels';
import cloneObject from '@/helpers/cloneObject';
import BaseModule from './generic/BaseModule.vue';
import TextAndMedia from './generic/TextAndMedia.vue';

export default {
  name: 'Services',
  components: {
    BaseModule,
    TextAndMedia,
  },
  mixins: [getApiHotels, base],
  inheritAttrs: false,
  props: {
    ...BaseModule.props,
    ...TextAndMedia.props,
    services: {
      type: Array,
      default() {
        return [];
      },
    },
    hotel: {
      type: Number,
      default: null,
    },
    hotelSelectListCheckbox: {
      type: Boolean,
      default: false,
    },
    hotelSelectListPhotosCheckbox: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    servicesList() {
      let list = '<ul class="services-module__list">';
      // TODO: Change to single icon component,
      // then change the component to use slots and a v-for li
      this.sortedServices.forEach((service) => {
        list += `
            <li class="services-module__list__item">
              <svg width="16" height="11" viewBox="0 0 16 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1.27539 5L5.73669 9L14.6593 1" stroke="#AAAAAA" stroke-width="2"/>
              </svg>
              ${service.serviceName}
            </li>
          `;
      });

      list += '</ul>';

      return list;
    },
    selectedHotel() {
      if (this.hotelSelectListCheckbox || this.hotelSelectListPhotosCheckbox) {
        return this.hotel
          ? this.hotels.filter((hotel) => hotel.Codigo === this.hotel)[0]
          : null;
      }
      return null;
    },
    hotelServices() {
      if (this.hotelSelectListCheckbox && this.selectedHotel) {
        const filteredServices = this.selectedHotel.Amenidades.filter(
          (service) => service.Nome,
        );
        return filteredServices.map((amenities) => ({
          serviceName: amenities.Nome,
        }));
      }
      return [];
    },
    allServices() {
      const services = cloneObject(this.services);
      return services.concat(this.hotelServices);
    },
    sortedServices() {
      const allServices = cloneObject(this.allServices);
      return allServices.sort((svc1, svc2) =>
        svc1.serviceName.localeCompare(svc2.serviceName),
      );
    },
    allSlides() {
      const hotelImages = [];

      if (
        this.hotelSelectListPhotosCheckbox &&
        this.selectedHotel &&
        this.selectedHotel.Imagens
      ) {
        if (this.selectedHotel.Imagens.ImagemPrincipal) {
          hotelImages.push({
            image: this.selectedHotel.Imagens.ImagemPrincipal,
            imageMobile:
              this.selectedHotel.Imagens.ImagemPrincipalMobile ||
              this.selectedHotel.Imagens.ImagemPrincipal,
          });
        }

        if (this.selectedHotel.Imagens.Galeria) {
          this.selectedHotel.Imagens.Galeria.forEach((image) => {
            hotelImages.push({
              image,
              imageMobile: image,
            });
          });
        }
      }

      return this.slides.concat(hotelImages);
    },
    ...mapGetters(['settings', 'hasLetsbookIntegration']),
  },
  mounted() {
    if (this.hasLetsbookIntegration) {
      this.getApiHotels();
    }
  },
  metadata: {
    extends: [BaseModule.metadata, TextAndMedia.metadata],
    tabs: [
      {
        name: 'content',
        groups: [
          {
            name: 'text',
            toRemove: ['text'],
          },
          {
            name: 'hotelData',
            position: 0,
            label: 'Dados do hotel',
            isEnabled({ hasLetsbookIntegration }) {
              return hasLetsbookIntegration;
            },
            fields: [
              {
                name: 'hotelSelectListCheckbox',
                type: 'Checkbox',
                label: 'Mostrar serviços do hotel',
              },
              {
                name: 'hotelSelectListPhotosCheckbox',
                type: 'Checkbox',
                label: 'Mostrar fotos do hotel',
              },
              {
                name: 'hotel',
                type: 'VueSelect',
                label: 'Selecione o hotel',
                isEnabled({
                  hotelSelectListCheckbox,
                  hotelSelectListPhotosCheckbox,
                }) {
                  return (
                    hotelSelectListCheckbox || hotelSelectListPhotosCheckbox
                  );
                },
                options() {
                  const hotelsToOptions = (hotels) =>
                    hotels.map((hotel) => ({
                      label: hotel.Nome,
                      value: hotel.Codigo,
                    }));

                  // Are hotels already loaded?
                  if (this.hotels.length > 0) {
                    return hotelsToOptions(this.hotels);
                  }

                  // THIS IS PURE MAGIC
                  return new Promise((resolve) => {
                    const unwatch = this.$watch('hotels', (hotels) => {
                      resolve(hotelsToOptions(hotels));
                      unwatch();
                    });
                  });
                },
              },
            ],
          },
          {
            name: 'buttons',
            label: 'Botões',
            fields: [
              {
                name: 'buttons',
                fields: [
                  {
                    name: 'buttonUrl',
                  },
                ],
              },
            ],
          },
          {
            name: 'additionalServices',
            label: 'Serviços adicionais',
            fields: [
              {
                name: 'services',
                type: 'List',
                itemLabel({ serviceName }) {
                  return serviceName || 'Serviço adicional {index}'; // any {index} will be replaced by item's index (1-based)
                },
                addItemLabel: 'Adicionar serviço',
                fields: [
                  {
                    name: 'serviceName',
                    type: 'ShortText',
                    label: 'Serviço',
                    default: '',
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
};
</script>

<style lang="scss">
.services {
  &__list {
    &__item {
      display: flex;
      gap: 5px;
      line-height: 1.5em;
      margin-bottom: 10px;
      align-items: baseline;

      svg {
        flex: 0 0 auto;
      }
    }
  }
}
</style>
